import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { ListViewDTO } from '@invenco/accounts-interface';

import { TableId } from '@/constants';
import { useGateways } from '@/gateways/GatewayProvider';
import { Result } from '@/shared/helpers/Result';
import { useGatewayMutation } from '@/shared/hooks/queries';
import { listViewsQueryOptions } from '@/shared/queries/listViewsQueryOptions';

import { FilterDefs, FilterState } from '../filters/types';
import { AddViewInput, UpdateViewInput, View, viewStateSchema } from './types';

type Props = {
  /** Unique ID for this table. Used to fetch and save views. */
  tableId: TableId;

  /** Static preset views to prefix before the saved views fetched from the backend. */
  presetViews?: View[];

  /**
   * Optional function to convert an old filter selection object for this table to the new filter state array format.
   *
   * TODO: remove this once all list pages with views have been migrated to the new data table component.
   */
  convertOldFilters?: (filters: Record<string, any>) => FilterState<FilterDefs>;
};

type Output = {
  isLoadingSavedViews: boolean;
  views: View[];
  onAddView: (input: AddViewInput) => Promise<Result<View>>;
  onUpdateView: (input: UpdateViewInput) => Promise<Result<View>>;
  onDeleteView: (viewId: string) => Promise<Result>;
};

const listViewToTableView = (
  listView: ListViewDTO,
  convertOldFilters?: Props['convertOldFilters'],
): View => {
  const stateResult = viewStateSchema.safeParse(listView.filters);
  if (stateResult.success) {
    return {
      id: listView.id,
      name: listView.name,
      state: stateResult.data,
      isSaved: true,
    };
  }

  // handle old object format
  let filters: FilterState<FilterDefs> = [];
  if (convertOldFilters && typeof listView.filters === 'object') {
    filters = convertOldFilters(listView.filters);
  }

  return {
    id: listView.id,
    name: listView.name,
    state: { filters },
    isSaved: true,
  };
};

export function useDataTableViews({ tableId, presetViews, convertOldFilters }: Props): Output {
  const queryClient = useQueryClient();
  const { accountsGateway } = useGateways();
  const { data: savedViews, isLoading } = useQuery(listViewsQueryOptions(accountsGateway, tableId));

  const views = useMemo(
    () => [
      ...(presetViews ?? []),
      ...(savedViews ?? []).map((savedView) => listViewToTableView(savedView, convertOldFilters)),
    ],
    [presetViews, savedViews, convertOldFilters],
  );

  const { mutate: addListView } = useGatewayMutation({
    mutationFn: ({ name, state }: AddViewInput) =>
      accountsGateway.createListView({ listId: tableId, name, filters: state }),
    onSuccess: (newView) => {
      queryClient.setQueryData<ListViewDTO[]>(
        ['listViews', { listId: tableId }],
        (existingViews) => [...(existingViews ?? []), newView],
      );
    },
  });

  const { mutate: updateListView } = useGatewayMutation({
    mutationFn: ({ id, name, state }: UpdateViewInput) =>
      accountsGateway.updateListView(id, { name, filters: state }),
    onSuccess: (updatedView) => {
      queryClient.setQueryData<ListViewDTO[]>(['listViews', { listId: tableId }], (existingViews) =>
        existingViews?.map((view) => (view.id === updatedView.id ? updatedView : view)),
      );
    },
  });

  const { mutate: deleteView } = useGatewayMutation({
    mutationFn: (viewId: string) => accountsGateway.deleteListView(viewId),
    onSuccess: (_, viewId: string) => {
      queryClient.setQueryData<ListViewDTO[]>(
        ['listViews', { listId: tableId }],
        (existingViews) => existingViews?.filter((view) => view.id !== viewId) ?? [],
      );
    },
  });

  const addView = async (input: AddViewInput) => {
    // translate from the internally used ListViewDTO to the View type
    const result = await addListView(input);
    if (result.isSuccess) {
      return Result.ok(listViewToTableView(result.value!));
    }
    return Result.fail(result.errorValue());
  };

  const updateView = async (input: UpdateViewInput) => {
    // translate from the internally used ListViewDTO to the View type
    const result = await updateListView(input);
    if (result.isSuccess) {
      return Result.ok(listViewToTableView(result.value!));
    }
    return Result.fail(result.errorValue());
  };

  return {
    views,
    isLoadingSavedViews: isLoading,
    onAddView: addView,
    onUpdateView: updateView,
    onDeleteView: deleteView,
  };
}
